import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useSelfieCyclesQuery } from '../features/snapshots/queries';
import { isEditableSelfieCycle } from '../utils/editableCycle';
import { getCurrentCycleFromList } from '../utils/selfiePeriod';

const SelfieCycleContext = createContext(null);

/**
 * Fonte única de ciclos selfie na app autenticada.
 * - `currentCycle`: ciclo em aberto (início no dia seguinte à última selfie).
 * - `selectedCycle` / `cycleIndex`: ciclo visível nas páginas com navegação.
 */
export function SelfieCycleProvider({ children }) {
  const { data: cycles = [], isLoading, isFetching, refetch } = useSelfieCyclesQuery();
  const [cycleIndex, setCycleIndex] = useState(0);

  const currentCycle = useMemo(() => getCurrentCycleFromList(cycles), [cycles]);

  const selectedCycle = useMemo(
    () => cycles[cycleIndex] ?? currentCycle ?? cycles[0] ?? null,
    [cycles, cycleIndex, currentCycle],
  );

  useEffect(() => {
    if (!cycles.length) {
      setCycleIndex(0);
      return;
    }
    const currentIdx = cycles.findIndex((c) => c.isCurrent);
    if (currentIdx >= 0) {
      setCycleIndex(currentIdx);
      return;
    }
    setCycleIndex(0);
  }, [cycles]);

  const selectCurrentCycle = useCallback(() => {
    const idx = cycles.findIndex((c) => c.isCurrent);
    if (idx >= 0) setCycleIndex(idx);
  }, [cycles]);

  const canEditSelected = useMemo(
    () => isEditableSelfieCycle(selectedCycle),
    [selectedCycle],
  );

  const canCreateSelfie = true;

  const periodRange = useMemo(() => {
    if (!selectedCycle?.start || !selectedCycle?.end) return null;
    return { start: selectedCycle.start, end: selectedCycle.end };
  }, [selectedCycle]);

  const value = useMemo(
    () => ({
      cycles,
      isLoading,
      isFetching,
      refetch,
      currentCycle,
      selectedCycle,
      cycleIndex,
      setCycleIndex,
      selectCurrentCycle,
      canEditSelected,
      canCreateSelfie,
      periodRange,
    }),
    [
      cycles,
      isLoading,
      isFetching,
      refetch,
      currentCycle,
      selectedCycle,
      cycleIndex,
      selectCurrentCycle,
      canEditSelected,
      canCreateSelfie,
      periodRange,
    ],
  );

  return (
    <SelfieCycleContext.Provider value={value}>{children}</SelfieCycleContext.Provider>
  );
}

export function useSelfieCycle() {
  const ctx = useContext(SelfieCycleContext);
  if (!ctx) {
    throw new Error('useSelfieCycle must be used within SelfieCycleProvider');
  }
  return ctx;
}
